import { useSelector } from "react-redux";
import { useEffect, useState, useRef, useCallback } from "react";
import type { RootState } from "./store/types";
import type { SSEOptions, SSEHookReturn, SSEMessage } from "./types";

export const useTypedSelector = useSelector.withTypes<RootState>();

export const useSSE = (
  url: string | null,
  options: SSEOptions,
): SSEHookReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const timestamp = () => new Date().toISOString();

    console.log(`[${timestamp()}] useSSE effect running with URL:`, url);
    if (!url) {
      console.log(`[${timestamp()}] No URL provided, skipping SSE connection`);
      return;
    }

    console.log(`[${timestamp()}] Creating new EventSource with URL:`, url);
    const eventSource = new EventSource(url, {
      withCredentials: true, // Include auth cookies
    });

    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log(`[${timestamp()}] SSE connection opened`);
      setIsConnected(true);
      options.onOpen?.();
    };

    eventSource.onmessage = (event) => {
      try {
        const data: SSEMessage = JSON.parse(event.data);
        console.log(`[${timestamp()}] SSE message received:`, data);

        // Handle heartbeat messages (ignore them)
        if (data.type === "heartbeat") {
          console.log(`[${timestamp()}] Received heartbeat, ignoring`);
          return;
        }

        // Close connection immediately for completion messages to prevent auto-reconnect
        if (data.type === "completed" || data.type === "stream_ended") {
          console.log(
            `[${timestamp()}] Sync completed - closing SSE connection immediately`,
          );
          eventSource.close();
          setIsConnected(false);
        }

        options.onMessage(data);
      } catch (error) {
        console.error(`[${timestamp()}] Failed to parse SSE message:`, error);
      }
    };

    eventSource.onerror = (error) => {
      // Only log error if the connection was not intentionally closed
      if (eventSource.readyState !== EventSource.CLOSED) {
        console.error(`[${timestamp()}] SSE connection error:`, error);
        options.onError?.(error);
      } else {
        console.log(`[${timestamp()}] SSE connection closed gracefully`);
      }
      setIsConnected(false);
    };

    return () => {
      console.log(`[${timestamp()}] SSE cleanup - closing connection`);
      if (eventSource.readyState !== EventSource.CLOSED) {
        eventSource.close();
      }
      setIsConnected(false);
    };
  }, [url]);

  const closeConnection = useCallback(() => {
    eventSourceRef.current?.close();
    setIsConnected(false);
  }, []);

  return { isConnected, closeConnection };
};
